import axios from "axios";
import { APIRoutes } from "../../../../generated/from-api/api-constants/route-constants";
import { API_BASE_URL } from "../../constants";

export type SendChannel = "email" | "sms";

export type SendStatus =
  | "pending"
  | "sent"
  | "delivered"
  | "opened"
  | "clicked"
  | "bounced"
  | "failed"
  | "blocked_dev"
  | "pending_unknown"
  | "complained";

export interface OutboundSendLogRow {
  id: string;
  channel: SendChannel;
  recipientEmail: string | null;
  recipientPhone: string | null;
  recipientUserProfileId: string | null;
  subject: string | null;
  body: string;
  templateName: string | null;
  senderAdminId: string;
  senderAdminEmail: string;
  status: SendStatus;
  providerMessageId: string | null;
  errorReason: string | null;
  devModeBlocked: boolean;
  metadata: Record<string, any>;
  created: string;
  modified: string;
}

export interface OutboundSendEventRow {
  id: string;
  outboundSendLogId: string;
  eventType: string;
  eventTimestamp: string;
  providerMessageId: string | null;
  reason: string | null;
  rawPayload: Record<string, any>;
  created: string;
}

export interface SendOneRequest {
  channel: SendChannel;
  recipient: string;
  subject?: string;
  body: string;
  templateName?: string;
  variables?: Record<string, string>;
}

export interface SendOneResponse {
  rowId: string;
  status: SendStatus;
  providerMessageId?: string;
}

export interface BulkRecipient {
  recipient: string;
  recipientUserProfileId?: string;
  variables?: Record<string, string>;
}

export interface SendBulkRequest {
  channel: SendChannel;
  template: string;
  subject?: string;
  recipients: BulkRecipient[];
  templateName?: string;
}

export interface SendBulkResponse {
  queued: number;
  sent: number;
  blockedDev: number;
  failed: number;
  rowIds: string[];
}

export interface LogQueryParams {
  recipient?: string;
  recipientEmail?: string;
  recipientPhone?: string;
  channel?: SendChannel;
  status?: string | string[];
  senderAdminId?: string;
  fromDate?: string;
  toDate?: string;
  limit?: number;
  offset?: number;
}

export interface LogQueryResponse {
  rows: OutboundSendLogRow[];
  total: number;
}

export interface LogDetailResponse {
  row: OutboundSendLogRow;
  events: OutboundSendEventRow[];
}

export const CommunicationsService = {
  SEND: async function (req: SendOneRequest): Promise<SendOneResponse> {
    return (
      await axios.post<SendOneResponse>(
        `${API_BASE_URL}${APIRoutes.ADMIN_COMMUNICATIONS_SEND}`,
        req
      )
    ).data;
  },
  SEND_BULK: async function (req: SendBulkRequest): Promise<SendBulkResponse> {
    return (
      await axios.post<SendBulkResponse>(
        `${API_BASE_URL}${APIRoutes.ADMIN_COMMUNICATIONS_BULK_SEND}`,
        req
      )
    ).data;
  },
  GET_LOG: async function (
    params: LogQueryParams = {}
  ): Promise<LogQueryResponse> {
    const search = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v === undefined || v === null) return;
      if (Array.isArray(v)) {
        search.set(k, v.join(","));
      } else {
        search.set(k, String(v));
      }
    });
    const qs = search.toString();
    return (
      await axios.get<LogQueryResponse>(
        `${API_BASE_URL}${APIRoutes.ADMIN_COMMUNICATIONS_LOG}${
          qs ? `?${qs}` : ""
        }`
      )
    ).data;
  },
  GET_LOG_DETAIL: async function (id: string): Promise<LogDetailResponse> {
    return (
      await axios.get<LogDetailResponse>(
        `${API_BASE_URL}${
          APIRoutes.ADMIN_COMMUNICATIONS_LOG
        }/${encodeURIComponent(id)}`
      )
    ).data;
  },
};
